import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, ProtectedRoute, NotificationProvider, useVendorQueries } from 'shared-lib';
import { VendorContextProvider } from './context';
import { useVendorContext } from './context/VendorContextProvider';
import { VendorDrawer, BottomNavigation, PageHeader } from './components';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Registration from './pages/Registration';
import ServiceRequests from './pages/ServiceRequests';
import ServiceRequestDetail from './pages/ServiceRequestDetail';
import IntegrateStripe from './pages/IntegrateStripe';
import './App.css';
import { Container } from '@mui/material';

// Separate the non-memoizable parts (hooks) and the JSX rendering
function useAppLogic() {
  // Keep all hooks at the top level, in the same order every time
  const { currentUser } = useAuth();
  const { vendors } = useVendorContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use useVendorQueries instead of useVendorAuthWithProfile
  const { useHasVendorAccess } = useVendorQueries();
  const { data: hasAccess = false, isLoading: isAccessLoading } = useHasVendorAccess();
  
  // Create an auth result compatible with ProtectedRoute
  const vendorAuthResult = useMemo(() => ({
    currentUser,
    loading: isAccessLoading,
    isAuthorized: hasAccess,
    profiles: vendors
  }), [currentUser, isAccessLoading, hasAccess, vendors]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Open drawer when returning from registration
  useEffect(() => {
    const justRegistered = location.state?.from === '/register';
    
    if (justRegistered) {
      setDrawerOpen(true);
      // Clear the location state
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Listen for custom event to open drawer
  useEffect(() => {
    const handleOpenDrawerEvent = () => {
      setDrawerOpen(true);
    };
    
    window.addEventListener('openVendorDrawer', handleOpenDrawerEvent);
    
    return () => {
      window.removeEventListener('openVendorDrawer', handleOpenDrawerEvent);
    };
  }, []);

  return {
    currentUser,
    drawerOpen,
    vendorAuthResult,
    closeDrawer
  };
}

const AppContent = React.memo(function AppContent() {
  const {
    currentUser,
    drawerOpen,
    vendorAuthResult,
    closeDrawer
  } = useAppLogic();
  
  return (
    <>
      {/* Page Header with current page name and actions */}
      <PageHeader />
      
      <Container component="main" sx={{ 
        mt: 2, 
        pb: 8,
        '& .MuiPaper-root': {
          borderRadius: '16px',
          border: '1px solid rgba(16, 29, 59, 0.2)',
          boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)'
        },
        '& .MuiButton-contained': {
          backgroundColor: '#00AB9B',
          borderRadius: '5px',
          padding: '10px 20px',
          transition: 'background-color 0.3s ease',
          '&:hover': {
            backgroundColor: '#00AB9B',
            filter: 'brightness(0.9)'
          }
        },
        '& .MuiButton-outlined': {
          borderColor: '#00AB9B',
          color: '#00AB9B',
          borderRadius: '5px',
          padding: '10px 20px',
          '&:hover': {
            borderColor: '#00AB9B',
            backgroundColor: 'rgba(0, 171, 155, 0.04)'
          }
        },
        '& .MuiTextField-root': {
          '& .MuiOutlinedInput-root': {
            borderRadius: '16px',
            '& fieldset': {
              borderColor: 'rgba(16, 29, 59, 0.2)'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(16, 29, 59, 0.3)'
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00AB9B'
            }
          },
          '& .MuiInputLabel-outlined': {
            backgroundColor: '#FFFFFF',
            paddingLeft: '4px',
            paddingRight: '4px',
          }
        },
        '& .MuiTypography-root': {
          color: '#101D3B'
        },
        '& .MuiTypography-h1, & .MuiTypography-h2, & .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
          fontWeight: 700
        }
      }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Registration />} />
          <Route path="/" element={
            <ProtectedRoute authResult={vendorAuthResult}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/requests" element={
            <ProtectedRoute authResult={vendorAuthResult}>
              <ServiceRequests />
            </ProtectedRoute>
          } />
          <Route path="/requests/:requestId" element={<ServiceRequestDetail />} />
          <Route path="/integrate-stripe" element={
            <ProtectedRoute authResult={vendorAuthResult}>
              <IntegrateStripe />
            </ProtectedRoute>
          } />
        </Routes>
      </Container>
      
      {/* Vendor Drawer */}
      <VendorDrawer 
        open={drawerOpen}
        onClose={closeDrawer}
      />
      
      {/* Bottom Navigation */}
      {currentUser && <BottomNavigation />}
    </>
  );
});

function App() {
  return (
    <div className="App">
      <NotificationProvider>
        <VendorContextProvider>
          <AppContent />
        </VendorContextProvider>
      </NotificationProvider>
    </div>
  );
}

export default App; 